// Component for displaying only Perplexity research models
import React, { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, Loader2, Check } from "lucide-react";

const ResearchModelsDropdown = ({
  models = [],
  selectedModel = "",
  onSelect = () => {},
  isLoading = false,
  placeholder = "Select a research model",
  forceOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(forceOpen);
  const dropdownRef = useRef(null);

  // Filter models to show only Perplexity models
  const researchModels = useMemo(() => {
    return models
      .filter(
        (model) => model.provider === "Perplexity" && model.active !== false
      )
      .map((model) => ({
        ...model,
        label: model.title || model.name || "Unknown Model",
        value: model._id,
        originalName: model.name,
      }));
  }, [models]);

  // Use all research models since search is removed
  const filteredModels = researchModels;

  // Auto-select first research model if none selected or if selected model is not a research model
  useEffect(() => {
    if (researchModels.length > 0) {
      const isSelectedModelAResearchModel = researchModels.some(
        (model) =>
          (model._id?.$oid || model.value || model.name) === selectedModel
      );

      if (!selectedModel || !isSelectedModelAResearchModel) {
        const firstModel = researchModels[0];
        onSelect(
          firstModel._id?.$oid ||
            firstModel._id ||
            firstModel.value ||
            firstModel.name
        );
      }
    }
  }, [researchModels, selectedModel, onSelect]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // If forceOpen changes, update isOpen accordingly
  useEffect(() => {
    if (forceOpen) setIsOpen(true);
  }, [forceOpen]);

  const handleSelect = (model) => {
    onSelect(model._id?.$oid || model.value || model.name);
    setIsOpen(false);
  };

  // Find selected model from research models
  const selected =
    filteredModels.find(
      (m) =>
        m._id?.$oid === selectedModel ||
        m.value === selectedModel ||
        m.name === selectedModel
    ) ||
    researchModels.find(
      (m) =>
        m._id?.$oid === selectedModel ||
        m.value === selectedModel ||
        m.name === selectedModel
    ) ||
    researchModels[0];

  return (
    <div className="research-models-dropdown relative w-full" ref={dropdownRef}>
      {!forceOpen && (
        <button
          type="button"
          className="research-models-dropdown-toggle w-full flex items-center justify-between px-4 py-2 text-sm font-medium bg-ac-02 text-white dark:text-black/70 hover:text-fg-80 hover:bg-ac-01 rounded-full transition-all duration-200 group gap-3"
          onClick={() => setIsOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          disabled={isLoading}
          title="Select Research Model"
        >
          <span className="flex items-center gap-2 truncate">
            <span>{selected?.label || selected?.name || placeholder}</span>
          </span>
          <ChevronDown
            size={12}
            className={`text-fg-60 opacity-70 text-white dark:text-black/70 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
      )}
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      {isOpen && (
        <div
          className="research-models-dropdown-list fixed md:absolute top-auto md:top-full bottom-0 md:bottom-auto left-0 right-0 md:left-0 md:right-auto md:mt-2 bg-white dark:bg-bg-60 rounded-t-2xl md:rounded-2xl shadow-xl border border-bd-50 z-50 overflow-hidden animate-slide-up md:animate-none md:w-96 md:max-w-md md:min-w-[320px]"
          style={{ 
            maxHeight: "85vh"
          }}
        >
          {/* Mobile header with close button */}
          <div className="flex items-center justify-between p-4 pb-2 md:hidden border-b border-bd-50">
            <h3 className="text-sm font-medium text-fg-50">Select Research Model</h3>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-lg text-fg-60 hover:text-fg-50 hover:bg-bg-50 transition-colors touch-manipulation"
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          {/* Content */}
          <div className="max-h-[60vh] md:max-h-[500px] overflow-y-auto custom-scrollbar bg-bg-50">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 size={24} className="animate-spin text-fg-60 mb-2" />
                <span className="text-sm text-fg-60">Loading models...</span>
              </div>
            ) : filteredModels.length === 0 ? (
              <div className="text-center text-fg-60 py-6 text-sm">
                No research models found
              </div>
            ) : (
              <div className="rounded-lg space-y-2 p-3 md:m-3">
                <div className="flex items-center gap-2 mb-2 md:mb-2">
                  <h4 className="text-sm font-medium text-fg-50 pb-1">
                    Research models
                  </h4>
                </div>
                {filteredModels.map((model, idx) => {
                  const isSelected =
                    selectedModel ===
                    (model._id?.$oid || model.value || model.name);
                  return (
                    <button
                      key={
                        model._id?.$oid ||
                        model._id ||
                        model.value ||
                        model.name + "-" + idx
                      }
                      type="button"
                      className={`w-full flex items-center justify-between p-3 md:p-2 rounded-lg text-left transition-colors group touch-manipulation ${
                        isSelected ? "bg-bg-40" : "hover:bg-bg-40"
                      }`}
                      onClick={() => handleSelect(model)}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm md:text-xs font-medium text-fg-50 break-words">
                            {model.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] md:text-[9px] text-fg-60 font-normal">
                            By {model.provider || "Unknown"}
                          </span>
                        </div>
                      </div>
                      {isSelected && <Check size={18} className="text-fg-50 flex-shrink-0 md:w-4 md:h-4" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearchModelsDropdown;
